import { Zap } from "lucide-react";

import { FormattedDate } from "@/components/common/FormattedDate";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { SurchargeNoticeOverlay } from "@/types/api";

interface SurchargeNoticeMarkProps {
  overlay: SurchargeNoticeOverlay;
  className?: string;
  /** Table cells: icon only; breakdown rows show icon + label. */
  compact?: boolean;
}

/** Compact indicator + popover for an active surcharge-notice adjustment. */
export function SurchargeNoticeMark({
  overlay,
  className,
  compact = false,
}: SurchargeNoticeMarkProps) {
  const dateLabel =
    overlay.date_type?.toUpperCase() === "ETA" ? "ETA" : "ETD";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex shrink-0 items-center gap-0.5 rounded px-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 hover:bg-amber-500/10 dark:text-amber-400",
            className,
          )}
          aria-label={`Surcharge notice: ${overlay.notice_title}`}
        >
          <Zap className="size-3 fill-current" aria-hidden />
          {!compact ? <span>Notice</span> : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 space-y-2 text-sm">
        <p className="font-semibold leading-snug">{overlay.notice_title}</p>
        <p className="text-xs text-muted-foreground">
          {overlay.code}: {overlay.original_amount.toLocaleString()} →{" "}
          {overlay.adjusted_amount.toLocaleString()} {overlay.currency}
        </p>
        <p className="text-xs text-muted-foreground">
          Effective <FormattedDate value={overlay.effective_date} />
          {overlay.expiry_date ? (
            <>
              {" "}
              – <FormattedDate value={overlay.expiry_date} />
            </>
          ) : null}{" "}
          ({dateLabel})
        </p>
      </PopoverContent>
    </Popover>
  );
}
